import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { Link, locationShape } from 'react-router';
import { FormattedMessage, injectIntl } from 'react-intl';
import * as constants from '../Authorization/constants';
import RepVisitsUnlockPremiumSidebar from '../RepVisitsUnlockPremiumSidebar';

import Dropdown from '../../components/Dropdown';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Icon from '../../components/Icon';
import Header from '../../components/Header';
import Form from '../../components/Form';
import Label from '../../components/Label';
import Segment from '../../components/Segment';
import PageTitle from '../../components/PageTitle';
import ActionableMessage from '../../components/ActionableMessage';
import SubscriptionActive from '../../components/SubscriptionActive';
import SubscriptionInactive from '../../components/SubscriptionInactive';

import HighSchoolAvailability from '../HighSchoolAvailability';
import { getWeek, getOrdinalDate, getWeekStart, getWeekEnd } from './date-utils';
import RepVisitsUpgradeForm from '../RepVisitsUpgradeForm';
import SimplifiedIntermediateHighSchoolResults from '../SimplifiedIntermediateHighSchoolSearchResults';
import InternationalHighSchoolResults from '../SimplifiedIntermediateHighSchoolSearchResults/international-search-results';
import RepVisitsYourSchedule from '../RepVisitsYourSchedule';
import messages from './messages';
import { searchOptionsConfig, premiumSearchOptionsConfig } from './searchOptions';

import * as SearchConstants from './constants';

import './styles.css';
import Popup from '../../components/Popup';

const searchOptions = searchOptionsConfig.map((each) => ({
  text: each.text,
  value: each.value,
}));

const MIN_FILTER_LENGTH = 2;

class RepVisitsSearchPage extends React.PureComponent {
  static propTypes = {
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    location: locationShape,
    searchType: PropTypes.array,
    router: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
    params: PropTypes.shape({
      id: PropTypes.string,
      availability: PropTypes.string,
    }),
  };

  constructor(props, context) {
    super(props, context);
    const date = new Date();
    date.setDate(date.getDate() + 7);
    this.state = {
      searchText: props.location.query.keywords,
      secondSearchText: '',
      fromDate: getWeekStart(date),
      toDate: getWeekEnd(date),
      openUpgradeModal: false,
      searchType: SearchConstants.SEARCH_BY_NAME,
      secondSearchType: SearchConstants.SEARCH_BY_CITY,
      defaultSearchByValue: SearchConstants.SEARCH_BY_NAME,
      defaultSecondSearchByValue: SearchConstants.SEARCH_BY_CITY,
      defaultSecondSearchByText: 'City',
      defaultSearchByText: 'Name',
      searchValidationError: false,
      secondSearchValidationError: false,
      showInternationalLink: false,
      secondSearchCriteria: false,
      scrollPosition: 0,
      isSecondSearchCriteriaDisplayed: false,
      secondSearchedText: null,
    };
    this.typeDropdown = null;
    this.typeSecondDropdown = null;
  }

  openUpgradeModal = (event) => {
    this.setState({ openUpgradeModal: true });
  };

  closeUpgradeModal = (event) => {
    this.setState({ openUpgradeModal: false });
  };

  setShowInternationalLink = (_searchType) => {
    if (_searchType === 'country') {
      this.setState({ showInternationalLink: true });
    } else {
      this.setState({ showInternationalLink: false });
    }
  };

  pushKeywordToRouter = (searchText) => {
    const { router } = this.props;
    router.push({
      pathname: '/rep-visits/search',
      query: {
        keywords: searchText,
      },
    });
  };

  search = (event) => {
    const { searchText, secondSearchText, secondSearchCriteria } = this.state;
    event.preventDefault();

    /* istanbul ignore else */
    if (this.validateSearch(searchText) && !secondSearchCriteria) {
      const _searchType = get(this.typeDropdown, 'state.value');
      this.setState({ searchType: _searchType, searchValidationError: false });
      this.setShowInternationalLink(_searchType);
      this.pushKeywordToRouter(searchText);
    } else if (this.validateSearch(searchText) && secondSearchCriteria && this.validateSearch(secondSearchText)) {
      const _searchType = get(this.typeDropdown, 'state.value');
      const _searchSecondType = get(this.typeSecondDropdown, 'state.value');
      const secondSearchedText = secondSearchText;
      this.setState({
        searchType: _searchType,
        searchValidationError: false,
        secondSearchType: _searchSecondType,
        secondSearchValidationError: false,
        isSecondSearchCriteriaDisplayed: true,
        secondSearchedText });
      this.setShowInternationalLink(_searchType);
      this.pushKeywordToRouter(searchText);
    } else if (secondSearchCriteria && !this.validateSearch(secondSearchText)) {
      this.setState({ secondSearchValidationError: true });
    } else {
      this.setState({ searchValidationError: true });
    }
  };

  validateSearch = (search) => search && search.length >= MIN_FILTER_LENGTH;

  handleInternationalClick = (event) => {
    const { router } = this.props;
    this.setState({ searchText: '' });
    router.push({
      pathname: '/rep-visits/search',
      query: {
        international: true,
      },
    });
  };

  handleSearchChange = (event) => {
    this.setState({ searchText: event.target.value });
  };

  handleSecondSearchChange = (event) => {
    this.setState({ secondSearchText: event.target.value });
  };

  handleDatesChange = (fromDate, toDate) => {
    this.setState({ fromDate, toDate });
  };

  handleScrollContextRef = (scrollContextRef) => {
    this.setState({ scrollContextRef });
  };

  handleDefaultSearchByValue = (event, data) => {
    event.stopPropagation();
    const selectedOption = searchOptions
      .concat(premiumSearchOptionsConfig)
      .find((el) => el.value === data.value);
    this.setState({ defaultSearchByValue: data.value, defaultSearchByText: selectedOption.text });
  };

  handleDefaultSecondSearchByValue = (event, data) => {
    const { defaultSearchByValue } = this.state;
    event.stopPropagation();
    const selectedOption = searchOptions
      .concat(premiumSearchOptionsConfig)
      .filter((option) => option.value !== defaultSearchByValue)
      .find((el) => el.value === data.value);
    this.setState({ defaultSecondSearchByValue: data.value, defaultSecondSearchByText: selectedOption.text });
  };

  preparePremiumDropdownOptions = () => searchOptions
    .concat(premiumSearchOptionsConfig)
    .map((option) => (
      {
        key: option.value,
        text: <span>{option.text}</span>,
        value: option.value,
      }
    ));

  preparePremiumDropdownOptionsForSecondSearch = () => {
    const { defaultSearchByValue } = this.state;
    return searchOptions
      .concat(premiumSearchOptionsConfig)
      .filter((option) => option.value !== defaultSearchByValue)
      .map((option) => (
        {
          key: option.value,
          text: <span>{option.text}</span>,
          value: option.value,
        }
      ));
  };

  getScrollPositionForHSResults = (scrollPosition) => {
    this.setState({ scrollPosition });
  };

  removeSecondSearchAtMouseClick = (e) => {
    e.preventDefault();

    // if the user presses the Enter key, do nothing
    // if the user clicks the Minus button, remove the second Search
    if (!(e.screenX === 0 && e.screenY === 0)) {
      this.setState({ secondSearchCriteria: false, isSecondSearchCriteriaDisplayed: false });
    }
  };

  render() {
    const { intl, router, location } = this.props;
    const { id, availability } = this.props.params;
    const {
      defaultSearchByValue,
      defaultSecondSearchByValue,
      defaultSearchByText,
      defaultSecondSearchByText,
      fromDate,
      toDate,
      searchText,
      secondSearchText,
      openUpgradeModal,
      scrollContextRef,
      searchType,
      secondSearchType,
      searchValidationError,
      secondSearchValidationError,
      showInternationalLink,
      scrollPosition,
      secondSearchCriteria,
      isSecondSearchCriteriaDisplayed,
      secondSearchedText,
    } = this.state;

    const keywords = get(location, 'query.keywords');
    const validSearch = this.validateSearch(keywords);
    const searchByAriaLabel = (defaultSearchByText || '').replace(/([.])/gi, '');
    const secondSearchByAriaLabel = (defaultSecondSearchByText || '').replace(/([.])/gi, '');

    const isInternationalResults = get(location, 'query.international');

    const currentDate = new Date();
    const fromDateValue = `${currentDate.getFullYear()}-${currentDate.getMonth() + 1}-${currentDate.getDate()}`;
    let searchResults;
    if (isInternationalResults) {
      searchResults = (
        <SubscriptionActive anyOf={constants.REP_VISITS_SUBSCRIPTION}>
          <Segment styleName="simplifiedIntermediateHighSchoolResultsContainer">
            <InternationalHighSchoolResults
              showPageTitle={!id}
              params={{
                fromDate: fromDateValue,
              }}
            />
          </Segment>
        </SubscriptionActive>
      );
    } else {
      searchResults = validSearch && (
      <Segment styleName="simplifiedIntermediateHighSchoolResultsContainer">
        <SimplifiedIntermediateHighSchoolResults
          scrollContextRef={scrollContextRef}
          getScrollPositionForHSResults={this.getScrollPositionForHSResults}
          scrollPosition={scrollPosition}
          showPageTitle={!id}
          params={isSecondSearchCriteriaDisplayed ? [
            {
              filter: keywords,
              searchType,
              fromDate: fromDateValue,
            }, {
              filter: secondSearchedText,
              searchType: secondSearchType,
              fromDate: fromDateValue,
            },
          ] :
            [{
              filter: keywords,
              searchType,
              fromDate: fromDateValue,
            }]}
        />
      </Segment>
      );
    }

    const notSubscribed = (validSearch || id) && (
    <SubscriptionInactive allOf={constants.REP_VISITS_SUBSCRIPTION}>
      <ActionableMessage
        action={this.openUpgradeModal}
        message={messages.repVisitsSubscriptionLimited}
        instructions={messages.repVisitsSubscriptionUpgrade}
      />
    </SubscriptionInactive>
    );

    const yourSchedule =
      (
        <div id="my-scheduld-block" styleName="schedule-block">
          <SubscriptionActive anyOf={constants.REP_VISITS_SUBSCRIPTION}>
            <RepVisitsYourSchedule
              params={{
                fromDate,
                fromDateOrdinal: getOrdinalDate(fromDate),
                toDate,
                toDateOrdinal: getOrdinalDate(toDate),
              }}
            />
          </SubscriptionActive>
          <SubscriptionInactive allOf={constants.REP_VISITS_SUBSCRIPTION}>
            <RepVisitsUnlockPremiumSidebar />
          </SubscriptionInactive>
        </div>
      );

    const trigger =
      (
        <div styleName="search-dropdown-wrapper" tabIndex="0">
          <label styleName="dropdown-header" htmlFor="premium-search">
            {intl.formatMessage(messages.searchByChoice)}
          </label>
          <div><span className="search-text">{defaultSearchByText}</span
          ><Icon name="caret down" color="teal" size="small" aria-label="carot-icon" />
          </div>
        </div>
      );

    const triggerForSecondSearchForm =
      (
        <div styleName="search-dropdown-wrapper" tabIndex="0">
          <label styleName="dropdown-header" htmlFor="premium-search">
            {intl.formatMessage(messages.searchByChoice)}
          </label>
          <div>
            <span className="search-text">
              {defaultSecondSearchByText !== defaultSearchByText ? defaultSecondSearchByText :
                searchOptions
                  .concat(premiumSearchOptionsConfig)
                  .find((option) => option.value !== defaultSearchByText).text}
            </span
          ><Icon name="caret down" color="teal" size="small" aria-label="carot-icon" />
          </div>
        </div>
      );

    return (
      <div id="search-container" styleName="container">
        {!searchResults && !id && (<PageTitle subTitle="Search and schedule" title="RepVisits" category="Intersect" />)}
        <div styleName="search-block-wrapper">
          <div styleName="search-block" ref={this.handleScrollContextRef}>
            <div styleName="top">
              <Header as="h1" styleName="header" id="pageTitle">
                <FormattedMessage id="repvisits.search.title" defaultMessage="Search" />&nbsp;&nbsp;
              </Header>
              <Form onSubmit={this.search} styleName={searchValidationError?'formError':'form'} className="formErrorButton">
                <Input
                  onChange={this.handleSearchChange}
                  onSubmit={this.search}
                  fluid
                  size="huge"
                  styleName="input"
                  value={searchText}
                  placeholder={intl.formatMessage(messages.searchForPlaceholder)}
                  input={{ 'aria-label': intl.formatMessage(messages.searchForPlaceholder) }}
                />
                <div styleName="search-action">
                  <div styleName="search-dropdown" data-cy="repvisit-search-closed-dropdown">
                    <SubscriptionActive anyOf={constants.REP_VISITS_SUBSCRIPTION}>
                      <Dropdown
                        id="premium-search"
                        styleName="dropdown-select"
                        aria-label={`Search by ${searchByAriaLabel}`}
                        icon={null}
                        value={defaultSearchByValue}
                        labeled
                        ref={(dropDown) => { this.typeDropdown = dropDown; }}
                        trigger={trigger}
                        onChange={this.handleDefaultSearchByValue}
                        options={this.preparePremiumDropdownOptions()}
                        selectOnNavigation={false}
                      />
                    </SubscriptionActive>
                    <SubscriptionInactive allOf={constants.REP_VISITS_SUBSCRIPTION}>
                      <Dropdown
                        id="non-premium-search"
                        styleName="dropdown-select"
                        aria-label={`Search by ${searchByAriaLabel}`}
                        icon={null}
                        value={defaultSearchByValue}
                        labeled
                        ref={(dropDown) => { this.typeDropdown = dropDown; }}
                        trigger={trigger}
                      >
                        <Dropdown.Menu styleName="dropdown-menu" data-cy="repvisit_search_dropdown">
                          {
                            searchOptions.map(
                              (option) => (
                                <Dropdown.Item
                                  onClick={this.handleDefaultSearchByValue}
                                  key={option.value}
                                  {...option}
                                />
                              ),
                            )
                          }
                          <Dropdown.Header styleName="premium-item premium-item-header" onClick={this.openUpgradeModal}>
                            <span data-cy="premium_search_pink" styleName="premium-item-header-text">Premium Search</span>
                            <Icon name="lock" className="right floated" />
                          </Dropdown.Header>
                          {
                            premiumSearchOptionsConfig.map(
                              (option) => (
                                <Dropdown.Item
                                  data-cy="premium_account_options_pink"
                                  onClick={this.openUpgradeModal}
                                  styleName="premium-item"
                                  key={option.value}
                                  {...option}
                                />
                              ),
                            )
                          }
                        </Dropdown.Menu>
                      </Dropdown>
                    </SubscriptionInactive>
                  </div>

                  {secondSearchCriteria ? (
                    <Button
                      icon="minus"
                      styleName="search-minus-button-icon"
                      aria-label="minus-btn"
                      id="repvisit-minus-btn"
                      onClick={this.removeSecondSearchAtMouseClick}
                    />
                  ) : (
                    <React.Fragment>
                      <Button icon="search" styleName="search-plus-button-icon" aria-label="search-btn" id="repvisit-search-btn" />
                      <SubscriptionActive anyOf={constants.REP_VISITS_SUBSCRIPTION}>
                        <Button
                          icon="plus"
                          styleName="search-plus-button-icon"
                          aria-label="plus-btn"
                          id="repvisit-plus-btn-one-search"
                          onClick={() => { this.setState({ secondSearchCriteria: true }); }}
                        />
                      </SubscriptionActive>
                      <SubscriptionInactive allOf={constants.REP_VISITS_SUBSCRIPTION}>
                        <Popup
                          position="top right"
                          size="mini"
                          trigger={(
                            <Button
                              icon="plus"
                              styleName="freemium-search-plus-button-icon"
                              aria-label="plus-btn"
                              id="freemium-repvisit-plus-btn"
                              onClick={this.removeSecondSearchAtMouseClick}
                            />
                          )}
                        >
                          <Popup.Content styleName="freemium-popup-content">
                            <Icon name="lock" color="red" />
                            <span styleName="fremium-popup-span">
                              Multiple search criteria is a <b style={{ color: '#d20061' }}>premium feature</b>.
                            </span>
                          </Popup.Content>
                        </Popup>
                      </SubscriptionInactive>
                    </React.Fragment>
                  )}
                </div>
              </Form>

              {secondSearchCriteria ? (
                <SubscriptionActive anyOf={constants.REP_VISITS_SUBSCRIPTION}>
                  <Form onSubmit={this.search} styleName={secondSearchValidationError ? 'formError-second-search' : 'form-second-criteria'} className="formErrorButton">
                    <div
                      styleName="AND-search-text"
                    >AND
                    </div>
                    <Input
                      onChange={this.handleSecondSearchChange}
                      onSubmit={this.search}
                      fluid
                      size="huge"
                      styleName="input-second-criteria"
                      value={secondSearchText}
                      placeholder={intl.formatMessage(messages.searchForPlaceholder)}
                      input={{ 'aria-label': intl.formatMessage(messages.searchForPlaceholder) }}
                      data-cy="second-search-input"
                    />
                    <div styleName="search-action">
                      <div styleName="search-dropdown" data-cy="repvisit-second-search-closed-dropdown">
                        <Dropdown
                          id="second-search-dropdown"
                          styleName="dropdown-select"
                          aria-label={`Search by ${secondSearchByAriaLabel}`}
                          icon={null}
                          value={defaultSecondSearchByValue}
                          labeled
                          ref={(dropDown) => { this.typeSecondDropdown = dropDown; }}
                          trigger={triggerForSecondSearchForm}
                          onChange={this.handleDefaultSecondSearchByValue}
                          options={this.preparePremiumDropdownOptionsForSecondSearch()}
                          selectOnNavigation={false}
                        />
                      </div>
                      <Button icon="search" styleName="search-plus-button-icon" aria-label="search-btn" id="repvisit-second-search-btn" />
                    </div>
                  </Form>
                </SubscriptionActive>
              ) :
                null}

              {validSearch && showInternationalLink && (
              <div styleName="want-a-complete-list">
                <FormattedMessage {...messages.completeList} />&nbsp;
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <Link onClick={this.handleInternationalClick}><FormattedMessage {...messages.allHighSchoolLink} /></Link>
              </div>
              )}
              {(searchValidationError || (secondSearchCriteria && secondSearchValidationError)) && (
              <Label role="tooltip" basic styleName="validationError" pointing>
                <Icon name="exclamation circle" styleName="validationError" size="large" />
                <FormattedMessage {...messages.searchValidation} />
              </Label>
              )}
              {searchResults}
              {id && (
              <HighSchoolAvailability
                router={router}
                schoolId={id}
                availability={availability}
                toDate={toDate}
                fromDate={fromDate}
                week={getWeek(fromDate)}
                onChangeDates={this.handleDatesChange}
                scrollContextRef={scrollContextRef}
              />
              )}
              {notSubscribed}
            </div>
          </div>
        </div>
        {yourSchedule}
        <RepVisitsUpgradeForm open={openUpgradeModal} onClose={this.closeUpgradeModal} />
      </div>
    );
  }
}

export default injectIntl(RepVisitsSearchPage);
